import { FC, memo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { handleFetchResponse } from 'api/errors'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { useOnMount } from 'hooks'
import { useAddCustomerMutation, useGetCustomerListQuery } from 'modules/customer/api'
import CustomerAddForm from 'modules/customer/components/CustomerAddForm/CustomerAddForm'
import { CustomerAddLocationKeys } from 'store/customerAdd/constants'

import { CustomerAddFormFields } from '../../components/CustomerAddForm/constants'
import { isSoleLegal } from '../../helpers'
import { customerDetailRoute, customerRoute } from '../../routes'

const STORE_KEY = CustomerAddLocationKeys.CustomerAdd
const CustomerAdd: FC = () => {
  const { closeModal, openModal } = useModal()
  const { refetch } = useGetCustomerListQuery()

  const navigate = useNavigate()

  const [addCustomer, { isLoading: isCustomerLoading }] = useAddCustomerMutation()

  const onClose = () => {
    closeModal(MODAL_IDS.AddCustomerPage)
    navigate(customerRoute)
  }

  const submitCustomer = async (rawPayload: CustomerAddFormFields) => {
    const { company, companySuggestorDisabled, addressSuggestorDisabled, ...payload } = rawPayload

    const isIp = isSoleLegal(company.inn)
    const isActiveManualEntryAddress = (isIp && addressSuggestorDisabled) || companySuggestorDisabled

    const payloadAddress = isActiveManualEntryAddress
      ? {
          address: {
            zipCode: payload.address.zipCode,
            localityName: payload.address.localityName,
            street: payload.address.street,
            house: payload.address.house,
            flat: payload.address.flat || undefined
          },
          legalAddress: undefined
        }
      : {
          legalAddress: isIp ? payload.address.value : company.address,
          address: undefined
        }
    const selectedOptionsPayload = companySuggestorDisabled
      ? {
          ...payload,
          dataSource: 'user',
          inn: company.inn,
          kpp: isIp ? company.kpp || undefined : company.kpp,
          name: company.value,
          ...payloadAddress
        }
      : {
          ...payload,
          dataSource: isActiveManualEntryAddress ? 'user' : 'auto',
          inn: company.inn,
          kpp: company.kpp,
          name: company.value,
          ...payloadAddress
        }

    // @ts-ignore
    const res = await addCustomer(selectedOptionsPayload).then(
      handleFetchResponse((res) => {
        onClose && onClose()
        navigate({ to: customerDetailRoute.to, params: { id: res.id } })
        refetch && refetch()
      })
    )
    // TODO подумать как сделать лучше
    const keysForTransform = ['inn', 'legalAddress', 'kpp', 'value']
    const companyErrorsTransformed = keysForTransform.reduce<Record<string, { title: string; path?: string }>>(
      (acc, key) => {
        if (res && res[key]) {
          acc[`company.${key}`] = { ...res[key] }
          delete res[key]
        }

        return acc
      },
      {}
    )

    if (res && Object.keys(companyErrorsTransformed).length) {
      const firstCompanyError = { company: companyErrorsTransformed[Object.keys(companyErrorsTransformed)[0]] }

      return { ...res, ...firstCompanyError, ...companyErrorsTransformed }
    }

    return { ...res, ...companyErrorsTransformed }
  }

  useOnMount(() => openModal(MODAL_IDS.AddCustomerPage))

  return (
    <ModalItem id={MODAL_IDS.AddCustomerPage}>
      <CustomerAddForm
        isCustomerLoading={isCustomerLoading}
        onClose={onClose}
        storeKey={STORE_KEY}
        // TODO типы
        // @ts-ignore
        submitCustomer={submitCustomer}
      />
    </ModalItem>
  )
}

export default memo(CustomerAdd)
